import { useLazyQuery, useMutation } from "@apollo/client";
import { Alert, Box, Grid } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FormSelect } from "components/Forms/FormSelect";
import { IssueListButton } from "components/Forms/IssueListButton";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import {
  GetWasteContainerQuery,
  Issue,
  IssueLevel,
  ReconcileChoice,
  WasteContainerStatus,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useCallback, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { hasCriticalIssues } from "util/forms";

import {
  CONTAINER_MUTATION_REFETCH_QUERIES,
  SET_CONTAINER_STATUS_MUTATION,
  VALIDATE_SET_CONTAINER_STATUS_INPUT,
} from "./api";
import {
  CONTAINER_STATUS_LABELS,
  RECONCILE_CHOICE_LABELS,
  STATUS_TRANSITION_OPTIONS,
} from "./containerDisplay";

type WasteContainerDetail = NonNullable<GetWasteContainerQuery["wasteContainer"]>;

type StatusFormValues = {
  status: WasteContainerStatus | null;
  reconcile: ReconcileChoice;
};

const RECONCILE_OPTIONS = Object.values(ReconcileChoice).map((choice) => ({
  value: choice,
  label: RECONCILE_CHOICE_LABELS[choice],
}));

export type SetContainerStatusDialogProps = {
  container: WasteContainerDetail;
  onClose: () => void;
  /**
   * Two of the reconcile choices are promises the user makes ("I'll log the
   * fill event", "I'll correct the capacity") — the parent opens the
   * matching dialog right after the status lands so the promise is easy to
   * keep.
   */
  onReconcileFollowUp: (choice: ReconcileChoice) => void;
};

/**
 * Server-side validation for a status change. A status the form hasn't picked
 * yet has nothing to validate, so it short-circuits rather than asking.
 */
function useStatusValidation(containerId: string) {
  const [validateQuery] = useLazyQuery(VALIDATE_SET_CONTAINER_STATUS_INPUT, {
    fetchPolicy: "no-cache",
  });
  return useCallback(
    async (values: StatusFormValues): Promise<Issue[]> => {
      if (!values.status) {
        return [];
      }
      const { data } = await validateQuery({
        variables: {
          input: { containerId, status: values.status, reconcile: values.reconcile },
        },
      });
      return data?.setContainerStatusInputValidator ?? [];
    },
    [validateQuery, containerId]
  );
}

export function SetContainerStatusDialog({
  container,
  onClose,
  onReconcileFollowUp,
}: SetContainerStatusDialogProps) {
  const alerts = useAlerts();

  const statusOptions = STATUS_TRANSITION_OPTIONS[container.status].map((status) => ({
    value: status,
    display: CONTAINER_STATUS_LABELS[status],
  }));

  const defaultValues = useMemo<StatusFormValues>(
    () => ({ status: null, reconcile: ReconcileChoice.KeepAsIs }),
    []
  );

  const validateInput = useStatusValidation(container.id);

  const form = useValidatingForm<StatusFormValues>(defaultValues, [], validateInput, undefined, {
    // A background refetch mid-edit re-issues the defaults; keep what the
    // user has typed rather than resetting under their cursor.
    keepDirtyValuesOnReset: true,
  });
  const { getValues, watch, issues, trigger } = form;

  const watchedStatus = watch("status");
  // The server's cue that this FULL mark sits below capacity — only then is
  // the reconcile question real.
  const needsReconcile = issues.some((issue) => issue.key === "reconcile");

  const [setStatus, { loading: isSaving }] = useMutation(SET_CONTAINER_STATUS_MUTATION, {
    refetchQueries: CONTAINER_MUTATION_REFETCH_QUERIES,
  });

  const onSubmit = async () => {
    const values = getValues();
    if (!values.status) {
      alerts.error("Select a status");
      return;
    }
    const status = values.status;
    const submitIssues = await validateInput(values);
    if (hasCriticalIssues(submitIssues)) {
      const firstCritical = submitIssues.find((issue) => issue.level === IssueLevel.Critical);
      alerts.error(firstCritical?.message ?? "Fix the critical issues before saving");
      return;
    }
    const reconciled = submitIssues.some((issue) => issue.key === "reconcile");
    // A save that outruns the debounced validation would silently submit the
    // default choice for a question the user never saw — surface the prompt
    // and let them answer instead.
    if (reconciled && !needsReconcile) {
      void trigger();
      alerts.warning("The recorded amount is below capacity — choose how to settle the difference");
      return;
    }
    await setStatus({
      variables: {
        input: {
          containerId: container.id,
          status,
          reconcile: reconciled ? values.reconcile : undefined,
        },
      },
      onCompleted: () => {
        alerts.success("Container status updated");
        onClose();
        if (
          reconciled &&
          (values.reconcile === ReconcileChoice.AddEvent ||
            values.reconcile === ReconcileChoice.AdjustCapacity)
        ) {
          onReconcileFollowUp(values.reconcile);
        }
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while updating the status");
      },
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      title="Change status"
      primaryActionButtonProps={{
        loading: isSaving,
        onClick: onSubmit,
        disabled: hasCriticalIssues(issues) || !watchedStatus,
      }}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
    >
      <SetContainerStatusFields
        form={form}
        statusOptions={statusOptions}
        needsReconcile={needsReconcile}
        issues={issues}
      />
    </Dialog>
  );
}

/**
 * The status pick, plus the reconcile question a below-capacity container
 * raises. Split from the dialog shell, which owns the mutation and when Save
 * is allowed to fire.
 */
function SetContainerStatusFields({
  form,
  statusOptions,
  needsReconcile,
  issues,
}: {
  form: ReturnType<typeof useValidatingForm<StatusFormValues>>;
  statusOptions: { value: string; display: string }[];
  /** True when marking FULL on a container holding less than its capacity. */
  needsReconcile: boolean;
  issues: Issue[];
}) {
  return (
    <FormProvider {...form}>
      <Box component="form" sx={{ pt: 1 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <FormSelect
              name="status"
              label="New status"
              control={form.control}
              selectItems={statusOptions}
            />
          </Grid>
          {needsReconcile && (
            <>
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning" variant="outlined">
                  {issues.find((issue) => issue.key === "reconcile")?.message ??
                    "The recorded amount is below capacity"}
                </Alert>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <RadioGroupField
                  name="reconcile"
                  label="How should the difference be settled?"
                  control={form.control}
                  radioOptions={RECONCILE_OPTIONS}
                  direction="column"
                  row={false}
                  // The reconcile issue is keyed to this field, so the group
                  // would echo the same message the Alert above already
                  // shows — suppress the duplicate.
                  hideErrors
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </FormProvider>
  );
}
